import { useRef, useEffect, useState, useCallback } from 'react'
import type { Annotation, AnnotationTool, RectAnnotation, ArrowAnnotation, TextAnnotation } from './types'

interface AnnotationCanvasProps {
  annotations: Annotation[]
  tool: AnnotationTool
  color: string
  onAddAnnotation: (annotation: Annotation) => void
}

interface DrawingState {
  startX: number
  startY: number
  currentX: number
  currentY: number
  isDrawing: boolean
}

export function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const headlen = 12
  const angle = Math.atan2(y2 - y1, x2 - x1)
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(
    x2 - headlen * Math.cos(angle - Math.PI / 6),
    y2 - headlen * Math.sin(angle - Math.PI / 6)
  )
  ctx.lineTo(
    x2 - headlen * Math.cos(angle + Math.PI / 6),
    y2 - headlen * Math.sin(angle + Math.PI / 6)
  )
  ctx.closePath()
  ctx.fill()
}

export function renderAnnotations(
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  width: number,
  height: number,
  clear = true
) {
  if (clear) ctx.clearRect(0, 0, width, height)
  for (const ann of annotations) {
    ctx.save()
    if (ann.type === 'rect') {
      ctx.strokeStyle = ann.color
      ctx.lineWidth = ann.strokeWidth
      ctx.strokeRect(
        ann.x * width,
        ann.y * height,
        ann.width * width,
        ann.height * height
      )
    } else if (ann.type === 'arrow') {
      ctx.strokeStyle = ann.color
      ctx.fillStyle = ann.color
      ctx.lineWidth = ann.strokeWidth
      drawArrow(ctx, ann.x1 * width, ann.y1 * height, ann.x2 * width, ann.y2 * height)
    } else if (ann.type === 'text') {
      ctx.fillStyle = ann.color
      ctx.font = `bold ${ann.fontSize}px sans-serif`
      ctx.fillText(ann.text, ann.x * width, ann.y * height)
    }
    ctx.restore()
  }
}

interface TextInputState {
  visible: boolean
  x: number
  y: number
  canvasX: number
  canvasY: number
}

export function AnnotationCanvas({
  annotations,
  tool,
  color,
  onAddAnnotation,
}: AnnotationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const drawingRef = useRef<DrawingState | null>(null)

  const [textInput, setTextInput] = useState<TextInputState>({
    visible: false,
    x: 0,
    y: 0,
    canvasX: 0,
    canvasY: 0,
  })
  const [textValue, setTextValue] = useState('')
  const textInputRef = useRef<HTMLInputElement>(null)

  // Redraw annotations whenever they change
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    renderAnnotations(ctx, annotations, canvas.width, canvas.height)
  }, [annotations])

  // Resize canvas to match container
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const observer = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect()
      canvas.width = rect.width
      canvas.height = rect.height
      const ctx = canvas.getContext('2d')
      if (ctx) renderAnnotations(ctx, annotations, canvas.width, canvas.height)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [annotations])

  const getCanvasCoords = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
      const canvas = canvasRef.current!
      const rect = canvas.getBoundingClientRect()
      return {
        x: (e.clientX - rect.left) / canvas.width,
        y: (e.clientY - rect.top) / canvas.height,
      }
    },
    []
  )

  const getClientCoords = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): { clientX: number; clientY: number } => ({
      clientX: e.clientX,
      clientY: e.clientY,
    }),
    []
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (tool === 'text') {
        // For text: show inline input at click position
        const containerRect = containerRef.current!.getBoundingClientRect()
        const canvasCoords = getCanvasCoords(e)
        const { clientX, clientY } = getClientCoords(e)
        setTextInput({
          visible: true,
          x: clientX - containerRect.left,
          y: clientY - containerRect.top,
          canvasX: canvasCoords.x,
          canvasY: canvasCoords.y,
        })
        setTextValue('')
        // Focus the input after React renders it, with a delay to bypass dialog focus trap
        requestAnimationFrame(() => {
          setTimeout(() => {
            textInputRef.current?.focus()
          }, 0)
        })
        return
      }

      const coords = getCanvasCoords(e)
      drawingRef.current = {
        startX: coords.x,
        startY: coords.y,
        currentX: coords.x,
        currentY: coords.y,
        isDrawing: true,
      };
      (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId)
    },
    [tool, getCanvasCoords, getClientCoords]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current?.isDrawing) return
      const coords = getCanvasCoords(e)
      drawingRef.current.currentX = coords.x
      drawingRef.current.currentY = coords.y

      // Draw preview
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      renderAnnotations(ctx, annotations, canvas.width, canvas.height)

      const { startX, startY, currentX, currentY } = drawingRef.current
      ctx.save()
      ctx.strokeStyle = color
      ctx.fillStyle = color
      ctx.lineWidth = 2

      if (tool === 'rect') {
        ctx.strokeRect(
          startX * canvas.width,
          startY * canvas.height,
          (currentX - startX) * canvas.width,
          (currentY - startY) * canvas.height
        )
      } else if (tool === 'arrow') {
        drawArrow(
          ctx,
          startX * canvas.width,
          startY * canvas.height,
          currentX * canvas.width,
          currentY * canvas.height
        )
      }
      ctx.restore()
    },
    [tool, color, annotations, getCanvasCoords]
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current?.isDrawing) return
      const coords = getCanvasCoords(e)
      const { startX, startY } = drawingRef.current
      drawingRef.current = null

      const dx = Math.abs(coords.x - startX)
      const dy = Math.abs(coords.y - startY)
      // Ignore tiny movements (accidental clicks)
      if (dx < 0.005 && dy < 0.005) return

      if (tool === 'rect') {
        const ann: RectAnnotation = {
          type: 'rect',
          x: Math.min(startX, coords.x),
          y: Math.min(startY, coords.y),
          width: Math.abs(coords.x - startX),
          height: Math.abs(coords.y - startY),
          color,
          strokeWidth: 2,
        }
        onAddAnnotation(ann)
      } else if (tool === 'arrow') {
        const ann: ArrowAnnotation = {
          type: 'arrow',
          x1: startX,
          y1: startY,
          x2: coords.x,
          y2: coords.y,
          color,
          strokeWidth: 2,
        }
        onAddAnnotation(ann)
      }
    },
    [tool, color, onAddAnnotation, getCanvasCoords]
  )

  const handleTextConfirm = useCallback(() => {
    if (textValue.trim()) {
      const ann: TextAnnotation = {
        type: 'text',
        x: textInput.canvasX,
        y: textInput.canvasY,
        text: textValue.trim(),
        color,
        fontSize: 16,
      }
      onAddAnnotation(ann)
    }
    setTextInput((prev) => ({ ...prev, visible: false }))
    setTextValue('')
  }, [textValue, textInput.canvasX, textInput.canvasY, color, onAddAnnotation])

  const cursorStyle =
    tool === 'text' ? 'text' : tool === 'rect' ? 'crosshair' : 'crosshair'

  return (
    <div ref={containerRef} className="absolute inset-0" style={{ pointerEvents: 'none' }}>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ cursor: cursorStyle, pointerEvents: 'auto' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
      {textInput.visible && (
        <input
          ref={textInputRef}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleTextConfirm()
            if (e.key === 'Escape') {
              setTextInput((prev) => ({ ...prev, visible: false }))
              setTextValue('')
            }
          }}
          onBlur={handleTextConfirm}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute bg-transparent border-none outline-none text-sm font-bold"
          style={{
            left: textInput.x,
            top: textInput.y - 16,
            color: color,
            pointerEvents: 'auto',
            minWidth: '80px',
            caretColor: color,
          }}
          placeholder="Type here..."
        />
      )}
    </div>
  )
}
